import React, { useContext, useState, useEffect } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import RecipeCard from '../components/recipes/RecipeCard';
import { RecipeContext, RecipeFilters } from '../contexts/RecipeContext';
import { Recipe } from '../types/Recipe';

const RecipesPage: React.FC = () => {
  const { recipes, loading, filterRecipes, removeRecipe } = useContext(RecipeContext);
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [timeFilter, setTimeFilter] = useState<number | undefined>(undefined);
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'alphabetical', 'time'

  useEffect(() => {
    // Appliquer les filtres
    const filters: RecipeFilters = {
      search: searchTerm,
      category: categoryFilter || undefined,
      difficulty: difficultyFilter || undefined,
      maxTime: timeFilter
    };

    let filtered = filterRecipes(filters);

    // Appliquer le tri
    switch (sortBy) {
      case 'alphabetical':
        filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'time':
        filtered = [...filtered].sort((a, b) => (a.prepTime + a.cookTime) - (b.prepTime + b.cookTime));
        break;
      case 'recent':
      default:
        // Supposons que les recettes les plus récentes ont les IDs les plus élevés
        filtered = [...filtered].sort((a, b) => parseInt(b.id) - parseInt(a.id));
        break;
    }

    setFilteredRecipes(filtered);
  }, [recipes, searchTerm, categoryFilter, difficultyFilter, timeFilter, sortBy, filterRecipes]);

  const handleEdit = (id: string) => {
    window.location.href = `/edit-recipe/${id}`;
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette recette ?')) {
      removeRecipe(id);
    }
  };

  const handleRecipeClick = (id: string) => {
    window.location.href = `/recipe/${id}`;
  };

  const resetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setDifficultyFilter('');
    setTimeFilter(undefined);
    setSortBy('recent');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow py-20 bg-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold mb-4">Mes Recettes</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Retrouvez toutes vos créations culinaires et filtrez-les selon vos préférences.
            </p>
          </div>
          
          {/* Barre de recherche */}
          <div className="mb-8">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Rechercher une recette..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 rounded-full border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          {/* Filtres et tri */}
          <div className="mb-8 bg-white rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap gap-4 justify-between items-center">
              <div className="flex flex-wrap gap-3">
                {/* Filtre par catégorie */}
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Toutes les catégories</option>
                  <option value="entrée">Entrées</option>
                  <option value="plat">Plats</option>
                  <option value="dessert">Desserts</option>
                  <option value="boisson">Boissons</option>
                </select>
                
                {/* Filtre par difficulté */}
                <select
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Toutes les difficultés</option>
                  <option value="facile">Facile</option>
                  <option value="moyen">Moyen</option>
                  <option value="difficile">Difficile</option>
                </select>
                
                {/* Filtre par temps */}
                <select
                  value={timeFilter?.toString() || ''}
                  onChange={(e) => setTimeFilter(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Tous les temps</option>
                  <option value="15">Moins de 15 min</option>
                  <option value="30">Moins de 30 min</option>
                  <option value="60">Moins de 1h</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                {/* Tri */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="recent">Plus récent</option>
                  <option value="alphabetical">A-Z</option>
                  <option value="time">Temps (croissant)</option>
                </select>
                
                {/* Bouton réinitialiser */}
                <button
                  onClick={resetFilters}
                  className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
          
          {/* Grille de recettes */}
          {loading ? (
            <div className="text-center py-10">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              <p className="mt-2 text-gray-600">Chargement des recettes...</p>
            </div>
          ) : filteredRecipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRecipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onClick={handleRecipeClick}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold mb-2">Aucune recette trouvée</h3>
              <p className="text-gray-600 mb-6">
                {recipes.length === 0
                  ? "Vous n'avez pas encore ajouté de recettes."
                  : "Aucune recette ne correspond à vos critères de recherche."}
              </p>
              {recipes.length === 0 && (
                <a
                  href="/add-recipe"
                  className="btn-primary inline-block"
                >
                  Ajouter ma première recette
                </a>
              )}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default RecipesPage;
